import { aiJson } from "@/services/ai.service";
import { sourceIndex, relevantText, type TopicSources } from "@/services/source.service";
import type { LessonDraft } from "@/services/path.service";
import type { Afb, Lesson, LessonPlan, Question, QuestionType, Reference } from "./types";
import { QUESTION_TYPES } from "./types";
import { isIconName, type IconName } from "@/lib/icons";

const ONLY_SOURCE =
  "ABSOLUTE REGEL: Verwende AUSSCHLIESSLICH Inhalte aus dem gelieferten MATERIAL. " +
  "Kein Vorwissen, keine erfundenen Beispiele, keine Begriffe die dort nicht vorkommen. " +
  'Jede Aufgabe bekommt "ref" (ID eines Quellblocks aus der Fundstellen-Liste) und ' +
  '"quote" (wörtliches Zitat, max 15 Wörter, aus genau diesem Block).';

const ICONS: IconName[] = [
  "book", "lightbulb", "target", "zap", "layers", "list", "search", "shuffle",
  "award", "check-square", "table", "code", "quote", "star", "pencil",
];

function iconFor(raw: unknown, i: number): IconName {
  const name = String(raw ?? "");
  return isIconName(name) ? name : ICONS[i % ICONS.length];
}

function afbOf(raw: unknown, fallback: Afb = 1): Afb {
  const n = Number(raw);
  return n === 2 || n === 3 ? (n as Afb) : fallback;
}

interface PlanReply {
  sequences?: {
    name?: string;
    lessons?: {
      title?: string; goal?: string; icon?: string; afb?: number;
      sources?: unknown; content?: unknown; kinds?: unknown;
    }[];
  }[];
}

const JUNK = /(inhaltsverzeichnis|gliederung|literaturverzeichnis|quellenverzeichnis|abbildungsverzeichnis|impressum|deckblatt|titelblatt|vorwort|anhang|stichwortverzeichnis|glossar der|seitenzahl|copyright)/i;

export function plannedSize(words: number): { seqs: number; perSeq: number } {
  const seqs = Math.max(1, Math.min(10, Math.round(words / 900)));
  const perSeq = words < 400 ? 3 : words < 3000 ? 4 : words < 12000 ? 5 : 6;
  return { seqs, perSeq };
}

/**
 * Groq lehnt (siehe GROQ_SAFE_PROMPT_TOKENS) eine einzelne Anfrage ab, sobald
 * Prompt + max_tokens zusammen ihr Minuten-Kontingent übersteigen. Das ganze
 * Material auf einmal zu schicken (früher: 28000 Zeichen) sprengt das bei
 * jedem nennenswerten Thema. Deshalb wird das Material in Bausteine dieser
 * Größe zerlegt – jeder Baustein ist eine eigene, kleine Anfrage.
 */
const CHUNK_CHARS = 8000;

/** Material in Bausteine zerlegen, ohne Quellblöcke zu vermischen. Ein Block, der selbst größer als ein Baustein ist (z. B. ein langes PDF), wird an Baustein-Grenzen weitergeschnitten. */
function materialChunks(src: TopicSources, maxChars: number): { index: string; text: string }[] {
  const chunks: { index: string; text: string }[] = [];
  let bufParts: string[] = [];
  let bufIds: string[] = [];
  let len = 0;
  const flush = () => {
    if (!bufParts.length) return;
    chunks.push({
      index: bufIds.map((id) => `${id} = ${src.blocks.find((b) => b.id === id)?.label ?? id}`).join("\n"),
      text: bufParts.join("\n\n"),
    });
    bufParts = [];
    bufIds = [];
    len = 0;
  };
  for (const b of src.blocks) {
    for (let i = 0; i < b.text.length; i += maxChars) {
      const piece = `## [${b.id}] ${b.label}\n${b.text.slice(i, i + maxChars)}`;
      if (len && len + piece.length > maxChars) flush();
      bufParts.push(piece);
      if (!bufIds.includes(b.id)) bufIds.push(b.id);
      len += piece.length;
      if (len >= maxChars) flush();
    }
  }
  flush();
  return chunks;
}

export async function planPath(
  topicTitle: string,
  src: TopicSources,
  existing: Lesson[] = []
): Promise<LessonDraft[]> {
  const drafts: LessonDraft[] = [];
  const seqOffset = existing.length ? Math.max(...existing.map((l) => l.seq_pos)) + 1 : 0;
  const seen = new Set(existing.map((l) => l.title.toLowerCase().trim()));
  const knownList = existing.map((l) => `${l.sequence} › ${l.title}`);
  const ids = new Set(src.blocks.map((b) => b.id));
  const strs = (v: unknown, max: number) =>
    Array.isArray(v) ? (v as unknown[]).map((x) => String(x).trim()).filter(Boolean).slice(0, max) : [];

  // Sequenzen gleichen Namens (case-insensitiv), die in verschiedenen
  // Bausteinen auftauchen, werden zu EINER Sequenz zusammengeführt – sonst
  // zerfiele ein Kapitel, das über zwei Materialstücke läuft, in zwei.
  const seqPosByName = new Map<string, number>();
  const seqOrderByPos = new Map<number, number>();
  const posCounter = new Map<number, number>();
  let nextSeqPos = seqOffset;

  const chunks = materialChunks(src, CHUNK_CHARS);
  for (const chunk of chunks) {
    const words = chunk.text.split(/\s+/).filter(Boolean).length;
    const { seqs, perSeq } = plannedSize(words);
    // Nur die letzten 40 – die Liste dient nur als Hinweis für die KI, nicht
    // als Dedup-Mechanismus (der läuft strikt über `seen`), und darf selbst
    // bei vielen Lektionen nicht ins Budget eines Bausteins hineinwachsen.
    const known = knownList.length
      ? "\n\nSCHON VORHANDEN (nicht wiederholen, nur echte Lücken ergänzen):\n" +
        knownList.slice(-40).map((t) => `- ${t}`).join("\n")
      : "";

    let reply: PlanReply;
    try {
      reply = await aiJson<PlanReply>(
        `Du bist Lerndidaktiker und gliederst EINEN AUSSCHNITT aus dem Material einer Schülerin/eines ` +
          `Schülers zum Thema „${topicTitle}" in Teile eines Lernpfads. ${ONLY_SOURCE}\n\n` +
          `UMFANG DIESES AUSSCHNITTS: rund ${words} Wörter. Erzeuge bis zu ${seqs} SEQUENZEN ` +
          `mit je ${perSeq} LEKTIONEN, NUR aus dem Stoff dieses Ausschnitts. Andere Materialausschnitte ` +
          `werden getrennt bearbeitet – wiederhole hier nichts, was nicht in DIESEM Text steht.\n\n` +
          "ÜBERSPRINGE alles, was kein Lernstoff ist: Inhaltsverzeichnis, Gliederung, Deckblatt, " +
          "Literatur-/Abbildungsverzeichnis, Impressum, Vorwort, Organisatorisches, Seitenzahlen. " +
          "Daraus entsteht NIE eine Lektion.\n" +
          "SEQUENZEN heissen wie Kapitel im Fachbuch (Beispiel: Variablen und Datentypen, " +
          "Kontrollstrukturen, Objektorientierung), nicht Abschnitt 1 oder Grundlagen. Setzt dieser " +
          "Ausschnitt ein Kapitel aus einem VORHERIGEN Ausschnitt fort, nutze exakt denselben Namen.\n" +
          "LEKTIONEN: ein abgrenzbarer Baustein, in 5 Minuten schaffbar, keine zwei doppelt.\n\n" +
          "Zu JEDER Lektion gehört ein Arbeitsauftrag für den Aufgaben-Generator:\n" +
          '- "sources": IDs der Quellblöcke, in denen dieser Stoff steht (aus der Fundstellen-Liste).\n' +
          '- "content": 3–6 Stichpunkte, welche Inhalte, Fachbegriffe und Kompetenzen die Lektion vermittelt.\n' +
          '- "kinds": welche Aufgabenarten passen, aus: Einführung, Begriffsklärung, Zuordnung, ' +
          "Anwendung, Analyse, Fehlersuche, Rechnung, Transfer, Bewertung.\n" +
          'Gib NUR kompaktes JSON: {"sequences":[{"name":"…","lessons":[{"title":"…","goal":"…",' +
          '"icon":"book","afb":1,"sources":["note:mitschrift"],"content":["…"],"kinds":["Einführung"]}]}]}\n' +
          `Erlaubte icon-Werte: ${ICONS.join(", ")}. Deutsch.` +
          known,
        `FUNDSTELLEN:\n${chunk.index}\n\nMATERIAL:\n${chunk.text}`,
        1800,
        "medium"
      );
    } catch (err) {
      // Ein gescheiterter Baustein darf die anderen nicht mitreißen – lieber
      // ein Lernpfad aus den übrigen Ausschnitten als gar keiner.
      console.error("[pfad] Materialausschnitt übersprungen", err);
      continue;
    }

    for (const seq of (reply.sequences ?? []).slice(0, 6)) {
      const name = (seq.name ?? "").trim() || `Abschnitt ${nextSeqPos - seqOffset + 1}`;
      if (JUNK.test(name)) continue;
      const key = name.toLowerCase();
      let seqPos = seqPosByName.get(key);
      if (seqPos === undefined) {
        if (nextSeqPos - seqOffset >= 14) continue; // insgesamt genug Sequenzen
        seqPos = nextSeqPos++;
        seqPosByName.set(key, seqPos);
        seqOrderByPos.set(seqPos, seqOrderByPos.size);
        posCounter.set(seqPos, 0);
      }
      const order = seqOrderByPos.get(seqPos)!;

      for (const les of (seq.lessons ?? []).slice(0, 10)) {
        if ((posCounter.get(seqPos) ?? 0) >= 12) break; // genug Lektionen in dieser Sequenz
        const title = (les.title ?? "").trim();
        if (!title || JUNK.test(title) || seen.has(title.toLowerCase())) continue;
        seen.add(title.toLowerCase());
        knownList.push(`${name} › ${title}`);
        const plan: LessonPlan = {
          sources: strs(les.sources, 6).filter((x) => ids.has(x)),
          content: strs(les.content, 8),
          kinds: strs(les.kinds, 5),
        };
        const pos = posCounter.get(seqPos) ?? 0;
        posCounter.set(seqPos, pos + 1);
        drafts.push({
          sequence: name,
          seq_pos: seqPos,
          position: pos,
          title,
          goal: (les.goal ?? "").trim(),
          icon: iconFor(les.icon, pos),
          afb: afbOf(les.afb, order === 0 ? 1 : order >= 2 ? 3 : 2),
          plan,
        });
      }
    }
  }

  return drafts;
}

/* ------------------------------ Aufgaben ------------------------------ */

const PEDAGOGY = `DIDAKTIK – halte dich strikt daran:
- AFB 1 = wiedergeben, AFB 2 = anwenden und verknuepfen, AFB 3 = beurteilen und uebertragen.
- Reihenfolge: 2 info-Karten zuerst, dann Aufgaben aufsteigend nach afb (etwa 40/40/20 %).
- Die erste info-Karte ist der Einstieg: 2-3 Saetze, worum es in DIESER Lektion geht, mit den noetigen Begriffen. Keine Begruessung, kein "In dieser Lektion lernst du".
- Jede Aufgabe prueft etwas ANDERES. Keine umformulierten Wiederholungen.
- explain: 1-2 Saetze, warum die Loesung stimmt.
- DISTRAKTOREN sind der wichtigste Teil: jede falsche Option muss eine echte, im Material belegbare Verwechslung sein - ein benachbarter Fachbegriff, eine haeufige Fehlvorstellung, eine aehnliche Definition aus einem anderen Kapitel. Gleiche Laenge und gleicher Satzbau wie die richtige Antwort. Verboten: erfundene Woerter, offensichtlich Absurdes, "alle genannten", "keine davon", Optionen die schon sprachlich nicht passen.`;

const TYPES_DOC = `AUFGABENTYPEN (nur diese neun, genau diese Felder):
{"type":"info","afb":1,"title":"…","body":["Absatz","Absatz"],"box":"optionaler Merksatz"}
{"type":"single","question":"Frage als ganzer Satz","options":["a","b","c","d"],"correct":0}
{"type":"multiple","question":"…","options":["a","b","c","d","e"],"correct":[0,2]}
{"type":"fill","question":"Kurzer Arbeitsauftrag OHNE Luecken","text":"Fliesstext mit {{1}} und {{2}}","blanks":["Loesung1","Loesung2"],"distractors":["falsch1","falsch2"]}
{"type":"match","question":"…","pairs":[["links","rechts"],["links2","rechts2"],["links3","rechts3"]]}
{"type":"calc","question":"…","answer":42,"unit":"m/s","tolerance":2,"solution":"Rechenweg"}
{"type":"error","question":"In welcher Zeile steckt der Fehler?","lines":["Zeile 1","Zeile 2","Zeile 3"],"badLine":1,"fixedLine":"Zeile 2 korrigiert"}
{"type":"code","question":"…","expected":"erwartete Eingabe","accept":["Alternative"]}
{"type":"table","question":"…","columns":["Begriff","Zuordnung"],"rows":[{"given":"A","correct":"X"}],"options":["X","Y","Z"]}

REGELN JE TYP:
- fill: "question" ist NUR der Arbeitsauftrag. Die Luecken {{1}},{{2}} stehen AUSSCHLIESSLICH in "text", nie in "question". Nummern lueckenlos ab 1, genauso viele wie blanks. Jede Luecke ist ein einzelnes Fachwort.
- single/multiple: "question" ist eine echte Frage, kein Lueckensatz.
- match: 3-5 Paare, alle rechten Seiten verschieden.
- calc nur, wenn im Material wirklich gerechnet wird.
- code: kurze freie Eingabe (Codezeile, Formel, Fachbegriff), nie ein ganzer Satz.
- error: die Zeilen bilden zusammen einen sinnvollen Text, genau eine Zeile ist falsch.`;

interface RawQuestion extends Record<string, unknown> {
  type?: string;
  afb?: number;
  ref?: string;
  quote?: string;
  explain?: unknown;
}

export async function lessonQuestions(
  lesson: Pick<Lesson, "title" | "goal" | "sequence" | "afb" | "plan">,
  src: TopicSources,
  count = 9
): Promise<Question[]> {
  const plan = lesson.plan;
  const focus = [lesson.sequence, lesson.title, lesson.goal, ...(plan?.content ?? [])].join(" ");
  const planned = plan?.sources?.length
    ? src.blocks.filter((b) => plan.sources.includes(b.id)).map((b) => `## [${b.id}] ${b.label}\n${b.text}`).join("\n\n")
    : "";
  // Gesenkt (war 18000) – zusammen mit PEDAGOGY/TYPES_DOC und dem
  // Antwort-Budget muss eine Anfrage unter Groqs Minuten-Kontingent bleiben
  // (siehe GROQ_SAFE_PROMPT_TOKENS in ai.service.ts); der Rest holt sich seine
  // Breite über mehrere kleine Runden statt eines großen Ausschnitts.
  const excerpt = (planned || relevantText(src, focus, 6000)).slice(0, 6000);
  const index = sourceIndex(src).slice(0, 3000);

  const brief =
    (plan?.content?.length ? `ZU VERMITTELNDE INHALTE:\n${plan.content.map((c) => `- ${c}`).join("\n")}\n` : "") +
    (plan?.kinds?.length ? `PASSENDE AUFGABENARTEN: ${plan.kinds.join(", ")}\n` : "");

  const ask = (n: number, extra: string) =>
    aiJson<RawQuestion[] | { questions?: RawQuestion[] }>(
      `Du erstellst die Aufgaben EINER Lektion eines Lernpfads.\n` +
        `Sequenz: ${lesson.sequence}\nLektion: ${lesson.title}\n` +
        `Lernziel: ${lesson.goal || "den Baustein sicher beherrschen"}\n` +
        `Schwerpunkt: AFB ${lesson.afb}\n${brief}\n` +
        `${ONLY_SOURCE}\n\n${PEDAGOGY}\n\n${TYPES_DOC}\n\n` +
        `Erzeuge GENAU ${n} Aufgaben. Behandle NUR den Stoff dieser Lektion. Antworte NUR mit ` +
        `{"questions":[…]}, kompakt, ohne Zeilenumbrueche im JSON. Deutsch.${extra}`,
      `FUNDSTELLEN:\n${index}\n\nMATERIAL (Ausschnitt zur Lektion):\n${excerpt}`,
      1800,
      "medium"
    );

  const known = new Set(src.blocks.map((b) => b.id));
  const labels = new Map(src.blocks.map((b) => [b.id, b.label] as const));
  const seen = new Set<string>();
  const clean: Question[] = [];

  const collectInto = (raw: RawQuestion[] | { questions?: RawQuestion[] }): void => {
    const list = Array.isArray(raw) ? raw : (raw?.questions ?? []);
    for (const r of list) {
      const q = sanitize(r, known, labels);
      if (!q) continue;
      const key = ("question" in q ? q.question : q.title).toLowerCase().replace(/\W+/g, " ").trim();
      if (seen.has(key)) continue;
      seen.add(key);
      clean.push(q);
    }
  };

  // Viele kleine Schritte statt einer Anfrage mit `count` Fragen auf einmal –
  // jede Runde bleibt so klar unter Groqs Grenze pro Anfrage.
  const ROUND_SIZE = 3;
  let remaining = count;
  while (remaining > 0) {
    const n = Math.min(ROUND_SIZE, remaining);
    try {
      collectInto(await ask(n, " Nutze verschiedene Typen, nie zweimal denselben hintereinander."));
    } catch (err) {
      console.error("[pfad] Aufgaben-Runde übersprungen", err);
    }
    remaining -= n;
  }
  if (clean.length < 3) {
    try {
      collectInto(
        await ask(6, ' Verwende NUR die Typen "info", "single" und "multiple". Halte dich exakt an deren Felder.')
      );
    } catch (err) {
      console.error("[pfad] Ausweich-Runde fehlgeschlagen", err);
    }
  }
  return order(clean);
}

export function order(list: Question[]): Question[] {
  const infos = list.filter((q) => q.type === "info").slice(0, 2);
  const rest = list.filter((q) => q.type !== "info").sort((a, b) => a.afb - b.afb);
  const out: Question[] = [...infos];
  let last = "";
  const pool = [...rest];
  while (pool.length) {
    const i = pool.findIndex((q) => q.type !== last);
    const pick = pool.splice(i >= 0 ? i : 0, 1)[0];
    last = pick.type;
    out.push(pick);
  }
  return out;
}

export function sanitize(
  r: RawQuestion,
  knownRefs: Set<string>,
  labels: Map<string, string>
): Question | null {
  const type = String(r.type ?? "") as QuestionType;
  if (!QUESTION_TYPES.includes(type)) return null;

  const afb = afbOf(r.afb, 1);
  const explain = typeof r.explain === "string" ? r.explain : undefined;

  const refId = String(r.ref ?? "");
  const importedSource = String(r.quelle ?? "").trim();
  const reference: Reference | undefined = knownRefs.has(refId)
    ? { sourceId: refId, label: labels.get(refId) ?? refId, snippet: String(r.quote ?? "") || undefined }
    : importedSource
      ? { sourceId: "", label: importedSource, snippet: String(r.zitat ?? "").trim() || undefined }
      : undefined;

  const base = { afb, explain, reference };
  const str = (v: unknown) => String(v ?? "").trim();
  const strList = (v: unknown, max: number) =>
    Array.isArray(v) ? (v as unknown[]).map(str).filter(Boolean).slice(0, max) : [];

  switch (type) {
    case "info": {
      const body = strList(r.body, 4);
      const title = str(r.title);
      if (!title || !body.length) return null;
      return { ...base, afb: 1, type, title, body, box: str(r.box) || undefined };
    }

    case "single": {
      const question = str(r.question);
      const options = strList(r.options, 5);
      const correct = Number(r.correct);
      if (!question || options.length < 3 || new Set(options).size !== options.length) return null;
      if (!(correct >= 0 && correct < options.length)) return null;
      return { ...base, type, question, options, correct };
    }

    case "multiple": {
      const question = str(r.question);
      const options = strList(r.options, 6);
      const correct = Array.isArray(r.correct)
        ? [...new Set((r.correct as unknown[]).map(Number))].filter((n) => n >= 0 && n < options.length)
        : [];
      if (!question || options.length < 3 || new Set(options).size !== options.length) return null;
      if (correct.length < 2 || correct.length >= options.length) return null;
      return { ...base, type, question, options, correct };
    }

    case "fill": {
      let question = str(r.question);
      let text = str(r.text);
      const blanks = strList(r.blanks, 5);
      if (!blanks.length) return null;
      if (/\{\{\d+\}\}/.test(question)) {
        if (!text) text = question;
        question = "Ergänze den Text.";
      }
      if (!text) return null;
      const marks = [...text.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
      if (marks.length !== blanks.length || marks.some((n, i) => n !== i + 1)) return null;
      if (blanks.some((b) => b.split(/\s+/).length > 3)) return null;
      return { ...base, type, question: question || "Ergänze den Text.", text, blanks, distractors: strList(r.distractors, 4) };
    }

    case "match": {
      const question = str(r.question);
      const pairs = Array.isArray(r.pairs)
        ? (r.pairs as unknown[])
            .map((p) => (Array.isArray(p) ? ([str(p[0]), str(p[1])] as [string, string]) : null))
            .filter((p): p is [string, string] => Boolean(p && p[0] && p[1]))
            .slice(0, 5)
        : [];
      if (!question || pairs.length < 3) return null;
      if (new Set(pairs.map((p) => p[1])).size !== pairs.length) return null;
      return { ...base, type, question, pairs };
    }

    case "calc": {
      const question = str(r.question);
      const answer = Number(r.answer);
      if (!question || !Number.isFinite(answer)) return null;
      const tol = Number(r.tolerance);
      return {
        ...base, type, question, answer,
        unit: str(r.unit) || undefined,
        tolerance: Number.isFinite(tol) && tol > 0 ? Math.min(tol, 25) : 1,
        solution: str(r.solution) || undefined,
      };
    }

    case "error": {
      const question = str(r.question) || "In welcher Zeile steckt der Fehler?";
      const lines = strList(r.lines, 8);
      const badLine = Number(r.badLine);
      const fixedLine = str(r.fixedLine);
      if (lines.length < 3 || !(badLine >= 0 && badLine < lines.length) || !fixedLine) return null;
      if (fixedLine === lines[badLine]) return null;
      return { ...base, type, question, lines, badLine, fixedLine };
    }

    case "code": {
      const question = str(r.question);
      const expected = str(r.expected);
      if (!question || !expected || expected.split(/\s+/).length > 12) return null;
      return { ...base, type, question, expected, starter: str(r.starter), accept: strList(r.accept, 4) };
    }

    case "table": {
      const question = str(r.question);
      const cols = strList(r.columns, 2);
      const rows = Array.isArray(r.rows)
        ? (r.rows as Record<string, unknown>[])
            .map((x) => ({ given: str(x?.given), correct: str(x?.correct) }))
            .filter((x) => x.given && x.correct)
            .slice(0, 6)
        : [];
      const options = [...new Set([...strList(r.options, 8), ...rows.map((x) => x.correct)])];
      if (!question || cols.length < 2 || rows.length < 2 || options.length < 3) return null;
      return { ...base, type, question, columns: [cols[0], cols[1]], rows, options };
    }
  }
  return null;
}
